import { useEffect } from "react";

interface HelmetProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schemaJsonLd?: Record<string, unknown>;
  additionalSchemas?: Record<string, unknown>[];
}

export function Helmet({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  schemaJsonLd,
  additionalSchemas,
}: HelmetProps) {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) canonicalEl.setAttribute("href", canonicalUrl);

    if (ogTitle) setMeta("og:title", ogTitle, true);
    if (ogDescription) setMeta("og:description", ogDescription, true);
    if (ogImage) setMeta("og:image", ogImage, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", canonicalUrl, true);

    setMeta("twitter:card", "summary_large_image");
    if (ogTitle) setMeta("twitter:title", ogTitle);
    if (ogDescription) setMeta("twitter:description", ogDescription);

    const scripts: HTMLScriptElement[] = [];
    const schemas = [schemaJsonLd, ...(additionalSchemas || [])].filter(Boolean);
    schemas.forEach((schema) => {
      const scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptEl);
      scripts.push(scriptEl);
    });

    return () => {
      document.title = originalTitle;
      scripts.forEach((s) => document.head.removeChild(s));
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, schemaJsonLd, additionalSchemas]);

  return null;
}